import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { DISCOUNT_CODE_PATH } from '../../../../config/paths';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import Table from '../../../common/Table';
import {
  DISCOUNT_CODE_CLIENT_COLUMN,
  DISCOUNT_CODE_EXCLUDE_DATE_COLUMN,
  DISCOUNT_CODE_ROUTE_COLUMN,
  DISCOUNT_CODE_SERVICE_TYPE_COLUMN,
  DISCOUNT_CODE_USER_COLUMN,
} from '../../../../config/columns';
import DiscountCodeBasicInformationResource from './resources/DiscountCodeBasicInformationResource';
import DiscountCodeToolbar from './DiscountCodeToolbar';
import Alert from '../../../common/informative/Alert';
import { GENERIC_NO_DATA_MESSAGE } from '../../../../config/messages';
import InternalResource from '../../../common/resource/InternalResource';
import BadgeList from '../../../common/BadgeList';
import { CONTRACT_TYPE } from '../../../../config/constants';
import {
  clearDiscountCode,
  getDiscountCode,
} from '../../../../actions/sales/DiscountCode';

export const DiscountCode = ({
  breadcrumbs,
  dispatchClearDiscountCode,
  dispatchGetDiscountCode,
  match: {
    params: { id: discountCodeId },
  },
  loading,
  discountCode,
}) => {
  useLayoutEffect(() => {
    dispatchGetDiscountCode({ discountCodeId });

    return () => dispatchClearDiscountCode();
  }, []);

  let content;
  let toolbar;

  if (loading) content = <Loader />;
  else if (discountCode.isEmpty())
    content = <NoDataResource returnPage={DISCOUNT_CODE_PATH} />;
  else {
    const discountCodeJson = discountCode.toJS();

    const tableExcludeDates =
      discountCodeJson.excludedDateRangeSet !== null &&
      discountCodeJson.excludedDateRangeSet.length !== 0 ? (
        <Table
          columns={DISCOUNT_CODE_EXCLUDE_DATE_COLUMN}
          data={discountCodeJson.excludedDateRangeSet}
          showPagination={false}
          defaultPageSize={discountCodeJson.excludedDateRangeSet.length}
        />
      ) : (
        <Alert type="warning" message={GENERIC_NO_DATA_MESSAGE} />
      );

    const tableIncludedRoutes =
      discountCodeJson.includedRouteSet !== null &&
      discountCodeJson.includedRouteSet.length !== 0 ? (
        <Table
          columns={DISCOUNT_CODE_ROUTE_COLUMN}
          data={discountCodeJson.includedRouteSet}
          showPagination={false}
          defaultPageSize={discountCodeJson.includedRouteSet.length}
        />
      ) : (
        <Alert type="warning" message={GENERIC_NO_DATA_MESSAGE} />
      );

    const tableExcludeRoutes =
      discountCodeJson.excludedRouteSet !== null &&
      discountCodeJson.excludedRouteSet.length !== 0 ? (
        <Table
          columns={DISCOUNT_CODE_ROUTE_COLUMN}
          data={discountCodeJson.excludedRouteSet}
          showPagination={false}
          defaultPageSize={discountCodeJson.excludedRouteSet.length}
        />
      ) : (
        <Alert type="warning" message={GENERIC_NO_DATA_MESSAGE} />
      );
    const tableClients =
      discountCodeJson.customerSet !== null &&
      discountCodeJson.customerSet.length !== 0 ? (
        <Table
          columns={DISCOUNT_CODE_CLIENT_COLUMN}
          data={discountCodeJson.customerSet}
          showPagination={false}
          defaultPageSize={discountCodeJson.customerSet.length}
        />
      ) : (
        <Alert type="warning" message={GENERIC_NO_DATA_MESSAGE} />
      );

    const includeAgencySet = discountCodeJson.includeAgencySet || [];

    const tableServiceType =
      discountCodeJson.discountCodeTripRuleList !== null &&
      discountCodeJson.discountCodeTripRuleList.length !== 0 ? (
        <Table
          columns={DISCOUNT_CODE_SERVICE_TYPE_COLUMN}
          data={discountCodeJson.discountCodeTripRuleList}
          showPagination={false}
          defaultPageSize={discountCodeJson.discountCodeTripRuleList.length}
        />
      ) : (
        <Alert type="warning" message={GENERIC_NO_DATA_MESSAGE} />
      );

    const agencyList = Array.isArray(includeAgencySet)
      ? includeAgencySet.map(({ name }) => ({
          text: name,
        }))
      : [];

    const agencyBadge =
      agencyList.length > 0 ? (
        <div className="mb-3">
          <BadgeList textArray={agencyList} />
        </div>
      ) : (
        <Alert type="warning" message={GENERIC_NO_DATA_MESSAGE} />
      );

    const tableUser =
      discountCodeJson.includeUserSet !== null &&
      discountCodeJson.includeUserSet.length !== 0 ? (
        <Table
          columns={DISCOUNT_CODE_USER_COLUMN}
          data={discountCodeJson.includeUserSet}
          showPagination={false}
          defaultPageSize={discountCodeJson.includeUserSet.length}
        />
      ) : (
        <Alert type="warning" message={GENERIC_NO_DATA_MESSAGE} />
      );

    const includeSecurityProfileSet =
      discountCodeJson.includeSecurityProfileSet || [];

    const securityProfileList = Array.isArray(includeSecurityProfileSet)
      ? includeSecurityProfileSet.map(({ name }) => ({
          text: name,
        }))
      : [];

    const securityProfileBadge =
      securityProfileList.length > 0 ? (
        <div className="mb-3">
          <BadgeList textArray={securityProfileList} />
        </div>
      ) : (
        <Alert type="warning" message={GENERIC_NO_DATA_MESSAGE} />
      );

    const agencyContractTypeDiscountCodeSet =
      discountCodeJson.agencyContractTypeDiscountCodeSet || [];

    const contractTypeList = Array.isArray(agencyContractTypeDiscountCodeSet)
      ? agencyContractTypeDiscountCodeSet.map(({ contractType }) => ({
          text: CONTRACT_TYPE[contractType].label,
        }))
      : [];

    const agencyContractTypeBadge =
      contractTypeList.length > 0 ? (
        <div className="mb-3">
          <BadgeList textArray={contractTypeList} />
        </div>
      ) : (
        <Alert type="warning" message={GENERIC_NO_DATA_MESSAGE} />
      );

    content = (
      <>
        <div className="row mb-4">
          <div className="col-md-8">
            <h3>Información Básica</h3>
            <DiscountCodeBasicInformationResource
              discountCode={discountCodeJson}
            />
          </div>
          <div className="col-md-4">
            <InternalResource
              id={discountCodeJson.id}
              createDate={discountCodeJson.createDate}
              lastUpdate={discountCodeJson.lastUpdate}
            />
          </div>
        </div>
        <div className="mt-4">
          <h3>Fechas Excluidas</h3>
          {tableExcludeDates}
        </div>
        <div className="mt-4">
          <h3>Rutas Incluidas</h3>
          {tableIncludedRoutes}
        </div>
        <div className="mt-4">
          <h3>Rutas Excluidas</h3>
          {tableExcludeRoutes}
        </div>
        <div className="mt-4">
          <h3>Servicios Incluidos</h3>
          {tableServiceType}
        </div>
        <div className="mt-4">
          <h3>Usuarios Incluidos</h3>
          {tableUser}
        </div>
        <div className="mt-4">
          <h3>Perfiles de Seguridad Incluidos</h3>
          {securityProfileBadge}
        </div>

        <div className="mt-4">
          <h3>Agencias Incluidas</h3>
          {agencyBadge}
        </div>
        <div className="mt-4">
          <h3>Tipo de Contrato de Agencias Incluidos</h3>
          {agencyContractTypeBadge}
        </div>
        <div className="mt-4">
          <h3>Clientes</h3>
          {tableClients}
        </div>
      </>
    );

    toolbar = <DiscountCodeToolbar discountCodeId={discountCodeJson.id} />;
  }

  return (
    <Content
      breadcrumbs={breadcrumbs}
      toolbar={toolbar}
      title="Código de Descuento"
      content={content}
    />
  );
};

DiscountCode.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchClearDiscountCode: PropTypes.func.isRequired,
  dispatchGetDiscountCode: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  match: matchPropTypes.isRequired,
  discountCode: PropTypes.instanceOf(Immutable.Map).isRequired,
};

DiscountCode.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ SalesUnit }) => ({
  breadcrumbs: [
    ...SalesUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Códigos de Descuento',
      href: DISCOUNT_CODE_PATH,
    },
    {
      text: 'Ver',
      href: '',
    },
  ],
  discountCode: SalesUnit.DiscountCode.getIn(['current', 'content']),
  loading: !SalesUnit.DiscountCode.getIn(['current', 'activity']).isEmpty(),
});

const mapDispatchToProps = {
  dispatchClearDiscountCode: clearDiscountCode,
  dispatchGetDiscountCode: getDiscountCode,
};

export default connect(mapStateToProps, mapDispatchToProps)(DiscountCode);
